import React from "react";
import { FieldErrors } from "react-hook-form";
import { Upload, X, Smartphone, Monitor } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { type CreateCampaignRouletteFormData } from "../../validations/campaign-roulette.validation";

interface BannersCardProps {
  errors: FieldErrors<CreateCampaignRouletteFormData>;
  banner3x4Preview: string | null;
  banner16x9Preview: string | null;
  onBanner3x4Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBanner16x9Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveBanner3x4: () => void;
  onRemoveBanner16x9: () => void;
}

export const BannersCard: React.FC<BannersCardProps> = ({
  errors,
  banner3x4Preview,
  banner16x9Preview,
  onBanner3x4Change,
  onBanner16x9Change,
  onRemoveBanner3x4,
  onRemoveBanner16x9,
}) => {
  const renderBannerUpload = (
    id: string,
    label: string,
    icon: React.ReactNode,
    aspectRatio: string,
    preview: string | null,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onRemove: () => void,
    error?: string
  ) => {
    const showPreview = preview;
    const showUploadArea = !preview;

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          {icon}
          <Label htmlFor={id}>{label}</Label>
        </div>

        {showPreview && (
          <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-2 bg-gray-50">
            <div
              className={`relative ${aspectRatio} w-full overflow-hidden rounded-md`}
            >
              <img
                src={preview}
                alt={`Preview ${label}`}
                className="w-full h-full object-cover"
              />
            </div>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onRemove}
              className="absolute top-4 right-4"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {showUploadArea && (
          <label
            htmlFor={id}
            className={`flex flex-col items-center justify-center ${aspectRatio} w-full border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors`}
          >
            <Upload className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm font-medium text-gray-600">
              Clique para fazer upload
            </span>
            <span className="text-xs text-gray-500 mt-1">PNG, JPG ou WEBP</span>
            <span className="text-xs text-gray-500">Máx. 5MB</span>
            <Input
              id={id}
              type="file"
              accept="image/*"
              onChange={onChange}
              className="hidden"
            />
          </label>
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Banners da Campanha</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderBannerUpload(
            "banner3x4",
            "Banner Mobile (3:4) *",
            <Smartphone className="h-4 w-4 text-gray-600" />,
            "aspect-[3/4]",
            banner3x4Preview,
            onBanner3x4Change,
            onRemoveBanner3x4,
            errors.banner3x4
              ? String(errors.banner3x4.message || "Erro no banner mobile")
              : undefined
          )}

          {renderBannerUpload(
            "banner16x9",
            "Banner Desktop (16:9) *",
            <Monitor className="h-4 w-4 text-gray-600" />,
            "aspect-[16/9]",
            banner16x9Preview,
            onBanner16x9Change,
            onRemoveBanner16x9,
            errors.banner16x9
              ? String(errors.banner16x9.message || "Erro no banner desktop")
              : undefined
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-800">
            💡 Recomenda-se usar imagens com proporções 3:4 para mobile e 16:9
            para desktop.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
